<template>
  <div
    ref="hero"
    class="dash-hero"
    :class="{ 'dash-hero--loading': loading }"
  >
    <div class="dash-hero__backdrop" aria-hidden="true">
      <span class="dash-hero__backdrop-base" />
      <span class="dash-hero__backdrop-dots" />
      <span class="dash-hero__backdrop-blob dash-hero__backdrop-blob--1" />
      <span class="dash-hero__backdrop-blob dash-hero__backdrop-blob--2" />
      <span class="dash-hero__backdrop-blob dash-hero__backdrop-blob--3" />
      <span class="dash-hero__backdrop-blob dash-hero__backdrop-blob--4" />
      <span class="dash-hero__backdrop-glow" />
      <span class="dash-hero__backdrop-sweep" />
      <span class="dash-hero__backdrop-shine" />
    </div>

    <div v-if="loading" class="dash-hero__panel dash-hero__panel--sk">
      <div class="dash-hero-sk">
        <div class="dash-hero-sk__line dash-hero-sk__line--sm" />
        <div class="dash-hero-sk__line dash-hero-sk__line--lg" />
        <div class="dash-hero-sk__line dash-hero-sk__line--md" />
        <div class="dash-hero-sk__aside">
          <div class="dash-hero-sk__clock" />
          <div class="dash-hero-sk__avatar" />
        </div>
      </div>
    </div>

    <div v-else class="dash-hero__panel dash-hero__panel--glass">
      <div class="dash-hero__panel-inner">
        <div class="dash-hero__content">
          <div class="dash-hero__greeting-row">
            <p class="dash-hero__greeting-line">
              <span class="dash-hero__status-dot" aria-hidden="true" />
              <feather-icon
                :icon="timeIcon"
                size="15"
                class="dash-hero__greeting-icon"
              />
              <span>{{ timeGreeting }}</span>
            </p>
          </div>

          <p class="dash-hero__kicker">{{ $t("Welcome back") }}</p>

          <h1 class="dash-hero__headline">
            <span class="dash-hero__name" :title="displayFirstName">{{
              displayFirstName
            }}</span>
          </h1>

          <p class="dash-hero__lead">{{ personalTagline }}</p>

          <div class="dash-hero__meta">
            <span class="dash-hero__meta-chip">
              <feather-icon icon="MapPinIcon" size="14" />
              {{ displayCampus }}
            </span>
            <span v-if="academicYearLabel" class="dash-hero__meta-chip dash-hero__meta-chip--outline">
              {{ academicYearLabel }}
            </span>
          </div>
        </div>

        <div class="dash-hero__presence">
          <div class="dash-hero__presence-inner">
            <div class="dash-hero__presence-clock">
              <div class="dash-hero__presence-head">
                <span class="dash-hero__live-tag">
                  <span class="dash-hero__live-pulse" aria-hidden="true" />
                  Live
                </span>
                <span class="dash-hero__clock-label">{{ $t("Local time") }}</span>
              </div>

              <div class="dash-hero__clock-line">
                <span class="dash-hero__clock-hm"
                  >{{ clockHours }}:{{ clockMinutes }}</span
                >
                <span class="dash-hero__clock-sec">:{{ clockSeconds }}</span>
                <span class="dash-hero__clock-ampm">{{ clockAmPm }}</span>
              </div>

              <p class="dash-hero__clock-date">{{ clockDate }}</p>
            </div>

            <div class="dash-hero__presence-divider" aria-hidden="true" />

            <div class="dash-hero__presence-profile">
              <div class="dash-hero__avatar-wrap">
                <b-avatar
                  v-if="profileSrc && !avatarFailed"
                  :src="profileSrc"
                  size="84px"
                  class="dash-hero__avatar"
                  @img-error="avatarFailed = true"
                />
                <b-avatar
                  v-else
                  size="84px"
                  :text="avatarInitials"
                  class="dash-hero__avatar dash-hero__avatar--fallback"
                />
              </div>
              <span
                class="dash-hero__avatar-caption"
                :title="profileFullLabel"
              >{{ profileShortLabel }}</span>
              <span class="dash-hero__presence-role">{{ $t("Signed in") }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BAvatar } from "bootstrap-vue";
import { avatarText } from "@core/utils/filter";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";

export default {
  name: "DashboardWelcomeHero",
  components: { BAvatar },
  props: {
    loading: { type: Boolean, default: false },
    userName: { type: String, default: "" },
    campusName: { type: String, default: "" },
    profileImage: { type: String, default: "" },
    academicYear: { type: [Number, String], default: "" },
  },
  data() {
    return {
      now: new Date(),
      clockTimeout: null,
      avatarFailed: false,
    };
  },
  computed: {
    displayFirstName() {
      return this.resolvePrimaryName(this.userName);
    },
    profileFullLabel() {
      return this.resolvePrimaryName(this.userName);
    },
    profileShortLabel() {
      return this.truncateLabel(this.profileFullLabel, 12);
    },
    displayCampus() {
      return String(this.campusName || "").trim() || "Campus";
    },
    academicYearLabel() {
      const y = this.academicYear;
      if (y === "" || y == null) return "";
      return `${this.$t("Session")} ${y}`;
    },
    timeTheme() {
      const hour = this.now.getHours();
      if (hour >= 5 && hour < 12) return "morning";
      if (hour >= 12 && hour < 17) return "day";
      if (hour >= 17 && hour < 21) return "evening";
      return "night";
    },
    timeGreeting() {
      const map = {
        morning: this.$t("Good morning"),
        day: this.$t("Good afternoon"),
        evening: this.$t("Good evening"),
        night: this.$t("Good night"),
      };
      return map[this.timeTheme] || map.day;
    },
    timeIcon() {
      const map = {
        morning: "SunriseIcon",
        day: "SunIcon",
        evening: "SunsetIcon",
        night: "MoonIcon",
      };
      return map[this.timeTheme] || "SunIcon";
    },
    personalTagline() {
      const campus = this.displayCampus;
      const fallbacks = {
        morning: `Here’s your morning overview for ${campus}.`,
        day: `Live numbers for ${campus} — collection, strength, and dues.`,
        evening: `End-of-day summary for ${campus}.`,
        night: `After-hours view for ${campus}. Stats update when you refresh.`,
      };
      const key = this.timeTheme;
      const custom = this.$t(`dashboard_hero_${key}`, { campus });
      if (custom && !String(custom).startsWith("dashboard_hero_")) {
        return custom;
      }
      return fallbacks[key] || fallbacks.day;
    },
    clockParts() {
      const parts = this.now.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      return parts;
    },
    clockHours() {
      const m = this.clockParts.match(/^(\d{1,2})/);
      return m ? m[1].padStart(2, "0") : "00";
    },
    clockMinutes() {
      const m = this.clockParts.match(/:(\d{2}):/);
      return m ? m[1] : "00";
    },
    clockSeconds() {
      const m = this.clockParts.match(/:(\d{2})\s/);
      return m ? m[1] : "00";
    },
    clockAmPm() {
      const m = this.clockParts.match(/\s(AM|PM)$/i);
      return m ? m[1] : "";
    },
    clockDate() {
      return this.now.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    profileSrc() {
      const raw = String(this.profileImage || "").trim();
      if (!raw) return "";
      if (raw.startsWith("blob:")) return raw;
      return getMediaPreviewUrl(raw, "Image");
    },
    avatarInitials() {
      return avatarText(this.displayFirstName);
    },
  },
  watch: {
    profileImage() {
      this.avatarFailed = false;
    },
  },
  mounted() {
    this.startClock();
    this.observeHeroVisibility();
    this._onVisibilityChange = () => {
      if (document.hidden) this.stopClock();
      else if (!this._heroVisibleOffscreen) this.startClock();
    };
    document.addEventListener("visibilitychange", this._onVisibilityChange);
  },
  beforeDestroy() {
    this.stopClock();
    if (this._onVisibilityChange) {
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
      this._onVisibilityChange = null;
    }
    if (this._heroIo) {
      this._heroIo.disconnect();
      this._heroIo = null;
    }
  },
  methods: {
    resolvePrimaryName(rawInput) {
      const raw = String(rawInput || "").trim();
      if (!raw) return "there";
      if (raw.includes("@")) {
        const local = raw.split("@")[0].replace(/[._-]/g, " ").trim();
        const word = local.split(/\s+/)[0] || local;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      const first = raw.split(/\s+/)[0] || raw;
      if (/^[A-Z0-9_]+$/.test(first) && first.length > 1) {
        return first.charAt(0) + first.slice(1).toLowerCase();
      }
      return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
    },
    truncateLabel(label, maxLen = 12) {
      const text = String(label || "").trim();
      if (!text || text.length <= maxLen) return text;
      return `${text.slice(0, Math.max(1, maxLen - 1))}…`;
    },
    startClock() {
      this.stopClock();
      // Tick on the second boundary instead of every 1000ms, and skip ticks
      // while the tab is hidden or the hero is scrolled out of view.
      const schedule = () => {
        this.clockTimeout = setTimeout(() => {
          this.now = new Date();
          schedule();
        }, 1000 - (Date.now() % 1000));
      };
      schedule();
    },
    stopClock() {
      if (this.clockTimeout) {
        clearTimeout(this.clockTimeout);
        this.clockTimeout = null;
      }
    },
    observeHeroVisibility() {
      if (typeof IntersectionObserver === "undefined") return;
      const el = this.$refs.hero;
      if (!el) return;
      this._heroIo = new IntersectionObserver(
        (entries) => {
          const visible = entries.some((e) => e.isIntersecting);
          this._heroVisibleOffscreen = !visible;
          // `is-paused` freezes every decorative animation while off-screen.
          if (el.classList) el.classList.toggle("is-paused", !visible);
          if (visible) {
            this.now = new Date();
            if (!document.hidden) this.startClock();
          } else {
            this.stopClock();
          }
        },
        { threshold: 0 }
      );
      this._heroIo.observe(el);
    },
  },
};
</script>

<style lang="scss" scoped>
.dash-hero {
  --hero-accent: #7367f0;
  --hero-accent-2: #9e95f5;
  --hero-accent-deep: #5e50ee;
  --hero-accent-soft: rgba(115, 103, 240, 0.12);
  --hero-text: #2f2b3d;
  --hero-text-muted: #6e6b7b;
  --hero-text-soft: #a8aaae;

  position: relative;
  margin-bottom: 2rem;
  border-radius: 22px;
  overflow: hidden;
  isolation: isolate;
  max-width: 100%;
  animation: dashHeroIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  background: #f8f7fc;
  border: 1px solid rgba(115, 103, 240, 0.1);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 12px 40px rgba(115, 103, 240, 0.08);

  &__backdrop-base {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      118deg,
      #f3efff 0%,
      #e8e0ff 22%,
      #ddd4ff 45%,
      #d4c8ff 68%,
      #e2d9ff 88%,
      #efe9ff 100%
    );
  }

  &__backdrop-dots {
    position: absolute;
    inset: 0;
    opacity: 0.5;
    background-image: radial-gradient(
      rgba(94, 80, 238, 0.16) 1px,
      transparent 1px
    );
    background-size: 20px 20px;
  }

  &__backdrop-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(56px);
    pointer-events: none;

    &--1 {
      width: 58%;
      height: 90%;
      top: -28%;
      left: -12%;
      background: rgba(158, 149, 245, 0.42);
    }

    &--2 {
      width: 52%;
      height: 85%;
      top: -15%;
      right: -8%;
      background: rgba(115, 103, 240, 0.38);
    }

    &--3 {
      width: 50%;
      height: 78%;
      bottom: -38%;
      left: 18%;
      background: rgba(185, 169, 248, 0.45);
    }

    &--4 {
      width: 45%;
      height: 100%;
      top: 0;
      right: -5%;
      background: rgba(126, 106, 247, 0.32);
    }
  }

  &__backdrop-glow {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 95% at 5% 45%, rgba(115, 103, 240, 0.2), transparent 58%),
      radial-gradient(ellipse 85% 100% at 95% 35%, rgba(94, 80, 238, 0.28), transparent 55%),
      radial-gradient(ellipse 70% 75% at 55% 105%, rgba(158, 149, 245, 0.18), transparent 50%);
  }

  &__backdrop-sweep {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 0%,
      transparent 42%,
      rgba(115, 103, 240, 0.12) 62%,
      rgba(126, 106, 247, 0.22) 100%
    );
    pointer-events: none;
  }

  &__backdrop-shine {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.5) 0%,
      rgba(255, 255, 255, 0.08) 38%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &__backdrop {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  &__panel {
    position: relative;
    z-index: 1;
    padding: 1.35rem 1.5rem;
    min-height: 0;

    &--glass {
      background: linear-gradient(
        105deg,
        rgba(255, 255, 255, 0.62) 0%,
        rgba(255, 255, 255, 0.48) 55%,
        rgba(237, 231, 255, 0.55) 100%
      );
      backdrop-filter: blur(14px) saturate(1.25);
      -webkit-backdrop-filter: blur(14px) saturate(1.25);
    }

    &--sk {
      min-height: 120px;
      background: rgba(255, 255, 255, 0.45);
    }
  }

  &__panel-inner {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 1.5rem 2rem;
    flex-wrap: wrap;
  }

  &__content {
    flex: 1 1 18rem;
    min-width: 0;
    max-width: 40rem;
    padding-top: 0.1rem;
    position: relative;
    z-index: 1;
    animation: dashHeroContentIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.06s both;
  }

  &__kicker {
    margin: 0 0 0.2rem;
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--hero-accent-deep);
  }

  &__greeting-row {
    margin-bottom: 0.6rem;
  }

  &__greeting-line {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0;
    padding: 0.35rem 0.85rem 0.35rem 0.6rem;
    border-radius: 999px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--hero-text);
    background: #fff;
    border: 1px solid rgba(115, 103, 240, 0.1);
    box-shadow: 0 2px 8px rgba(34, 41, 47, 0.04);
  }

  &__greeting-icon {
    color: var(--hero-accent);
  }

  &__status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #28c76f;
    box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
  }

  &__headline {
    margin: 0 0 0.5rem;
    background: none;
  }

  &__name {
    display: block;
    font-size: clamp(2rem, 4.5vw, 2.65rem);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1.08;
    color: var(--hero-text);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__lead {
    margin: 0 0 1rem;
    max-width: 34rem;
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.55;
    color: var(--hero-text-muted);
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.55rem;
  }

  &__meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--hero-accent-deep);
    background: #fff;
    border: 1px solid rgba(115, 103, 240, 0.14);
    box-shadow: 0 1px 4px rgba(34, 41, 47, 0.04);

    &--outline {
      color: var(--hero-text-muted);
      background: rgba(255, 255, 255, 0.65);
      border: 1px solid rgba(34, 41, 47, 0.06);
      font-weight: 500;
    }
  }

  &__presence {
    position: relative;
    flex: 0 1 auto;
    align-self: center;
    min-width: 0;
    max-width: min(100%, 24.5rem);
    padding: 4px;
    border-radius: 20px;
    background: linear-gradient(
      145deg,
      rgba(115, 103, 240, 0.22) 0%,
      rgba(158, 149, 245, 0.15) 100%
    );
    box-shadow: 0 10px 28px rgba(94, 80, 238, 0.12);
    animation: dashHeroContentIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
  }

  &__presence-inner {
    display: flex;
    align-items: stretch;
    gap: 0;
    padding: 1rem 0.85rem 1.05rem 1.05rem;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.95);
    min-width: 0;
    min-height: 7.25rem;
  }

  &__presence-divider {
    width: 1px;
    align-self: stretch;
    margin: 0.5rem 0.75rem 0.5rem 0.65rem;
    background: linear-gradient(
      180deg,
      transparent,
      rgba(115, 103, 240, 0.2) 20%,
      rgba(115, 103, 240, 0.2) 80%,
      transparent
    );
    flex-shrink: 0;
  }

  &__presence-clock {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__clock-line {
    display: flex;
    align-items: baseline;
    flex-wrap: nowrap;
    gap: 0.2rem;
    font-variant-numeric: tabular-nums;
    margin-bottom: 0.35rem;
  }

  &__clock-hm {
    font-size: clamp(1.5rem, 2.4vw, 1.85rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    color: var(--hero-text);
    line-height: 1.1;
  }

  &__clock-sec {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--hero-accent);
  }

  &__presence-orb {
    display: none;
  }

  &__presence-grid {
    display: none;
  }

  &__time-blocks,
  &__time-block,
  &__time-sep,
  &__time-tail,
  &__time-sec {
    display: none;
  }

  &__presence-head {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.45rem;
    margin-bottom: 0.55rem;
    flex-wrap: wrap;
  }

  &__live-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.625rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.12rem 0.45rem 0.12rem 0.35rem;
    border-radius: 999px;
    color: #28c76f;
    background: rgba(40, 199, 111, 0.1);
    border: 1px solid rgba(40, 199, 111, 0.18);
  }

  &__live-pulse {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #28c76f;
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.45);
    animation: dashLivePulse 2s ease-out infinite;
  }

  &__time-blocks {
    display: flex;
    align-items: flex-end;
    gap: 0.35rem;
    font-variant-numeric: tabular-nums;
  }

  &__time-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 3.1rem;
    padding: 0.45rem 0.55rem 0.35rem;
    border-radius: 12px;
    background: linear-gradient(180deg, #f8f7fc 0%, #fff 100%);
    border: 1px solid rgba(115, 103, 240, 0.12);
    box-shadow: 0 4px 12px rgba(94, 80, 238, 0.08);
  }

  &__time-block-val {
    font-size: 1.65rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.03em;
    color: var(--hero-text);
  }

  &__time-block-lbl {
    margin-top: 0.2rem;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--hero-text-soft);
  }

  &__time-sep {
    font-size: 1.35rem;
    font-weight: 800;
    color: var(--hero-accent);
    padding-bottom: 1.15rem;
    line-height: 1;
  }

  &__time-tail {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-bottom: 0.35rem;
    margin-left: 0.15rem;
  }

  &__time-sec {
    font-size: 1.05rem;
    font-weight: 800;
    color: var(--hero-accent-deep);
    letter-spacing: -0.02em;
  }

  &__presence-profile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex: 0 0 6.5rem;
    width: 6.5rem;
    min-width: 0;
    padding: 0.15rem 0 0;
  }

  &__avatar-wrap {
    position: relative;
    width: 5.25rem;
    height: 5.25rem;
    flex-shrink: 0;
    padding: 3px;
    border-radius: 50%;
    background: linear-gradient(145deg, #7367f0, #b9b3f8);
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.25);
  }

  &__avatar-ring {
    display: none;
  }

  &__avatar {
    position: relative;
    z-index: 1;
    width: 100% !important;
    height: 100% !important;
    border: 2px solid #fff !important;
    box-shadow: none !important;
    font-size: 1.35rem !important;

    &--fallback {
      background: linear-gradient(145deg, #ede9fe, #c4b5fd) !important;
      color: #5e5873 !important;
      font-weight: 800 !important;
    }
  }

  &__avatar-caption {
    margin-top: 0.4rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: none;
    color: var(--hero-text);
    width: 100%;
    max-width: 100%;
    padding: 0 0.1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.25;
  }

  &__clock-label {
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--hero-text-soft);
  }

  &__clock-ampm {
    margin-left: 0.15rem;
    font-size: 0.6875rem;
    font-weight: 700;
    color: var(--hero-accent);
    align-self: flex-end;
    padding-bottom: 0.1rem;
  }

  &__clock-date {
    margin: 0.2rem 0 0;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--hero-text-soft);
    line-height: 1.35;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__presence-role {
    margin-top: 0.12rem;
    font-size: 0.5625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--hero-accent);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.dash-hero-sk {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-start;

  &__line {
    border-radius: 8px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.08) 100%
    );
    background-size: 200% 100%;
    animation: dashSk 1.4s ease-in-out infinite;

    &--sm {
      width: 35%;
      height: 12px;
    }
    &--lg {
      width: 70%;
      height: 36px;
    }
    &--md {
      width: 50%;
      height: 14px;
    }
  }

  &__aside {
    margin-left: auto;
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  &__clock {
    width: 188px;
    height: 96px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.1);
    animation: dashSk 1.4s ease-in-out infinite;
  }

  &__avatar {
    width: 104px;
    height: 104px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    animation: dashSk 1.4s ease-in-out infinite;
  }
}

@keyframes dashHeroIn {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes dashHeroContentIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes dashLivePulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.45);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(40, 199, 111, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0);
  }
}

@keyframes dashColonBlink {
  50% {
    opacity: 0.25;
  }
}

@keyframes dashSk {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@media (max-width: 991px) {
  .dash-hero__presence {
    width: 100%;
    max-width: 26rem;
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 767px) {
  .dash-hero__panel {
    padding: 1.25rem 1.15rem;
  }

  .dash-hero__presence-inner {
    flex-wrap: wrap;
    justify-content: center;
    padding: 0.85rem;
  }

  .dash-hero__presence-divider {
    display: none;
  }

  .dash-hero__presence-profile {
    flex: 1 1 100%;
    width: 100%;
    max-width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.35rem 0.65rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(115, 103, 240, 0.1);
  }

  .dash-hero__avatar-caption {
    max-width: min(100%, 14rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dash-hero,
  .dash-hero__content,
  .dash-hero__presence,
  .dash-hero__live-pulse {
    animation: none !important;
  }
}

.dash-hero.is-paused .dash-hero__panel--glass,
.dash-hero.is-paused .dash-hero__presence-inner,
.dash-hero.is-paused .dash-hero__greeting-line {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.dash-hero.is-paused .dash-hero__live-pulse {
  animation-play-state: paused !important;
}
</style>
